import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Environment, EnvironmentService, getEnvironment } from '@innovation-os-ng/core/utils';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { metricSwitchOptionsMap } from '../../../../definitions/metricSwitchOptions';
import { MetricOptions, MetricsAggregation, MetricsSwitch } from '../../../model/metric';

// Threshold for using smart relevance, dependent on environment
const FEED_LIMIT = {
  [Environment.test]: 10_000,
  [Environment.stage]: 40_000,
  [Environment.live]: 40_000
};

@Injectable({
  providedIn: 'root'
})
export class MetricService implements OnDestroy {
  /*
    For now, we want to show by default Document count
  */
  private metricSwitch: MetricsSwitch = MetricsSwitch.COUNT;
  private readonly metricSwitchChange: Subject<void> = new Subject();
  private readonly routerSubscription: Subscription;

  // Metrics that are relevance-based
  private readonly relevanceMetrics = [
    MetricsAggregation.RELEVANCE,
    MetricsAggregation.SMART_RELEVANCE,
    MetricsAggregation.COUNT_LOG_AND_TOPIC_SCORE,
    MetricsAggregation.COUNT_LOG_AND_RELEVANCE
  ];

  // Maintains current status of whether the complex metrics are disabled (due to a high number of signals)
  public complexMetricsDisabled = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly environmentService: EnvironmentService
  ) {
    this.routerSubscription = this.route.queryParams.subscribe((params) => {
      if (params?.metric && this.metricSwitch !== params?.metric) {
        this.metricSwitch = params.metric;
        this.metricSwitchChange.next();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public getGlobalMetric(): MetricOptions {
    return metricSwitchOptionsMap[this.metricSwitch];
  }

  public getMetricSwitch() {
    return this.metricSwitch;
  }

  public metricChange(metricsSwitch: MetricsSwitch) {
    const urlParameters = Object.assign({}, this.route.snapshot.queryParams);
    urlParameters.metric = metricsSwitch;
    this.router.navigate([], { relativeTo: this.route, queryParams: urlParameters });
  }

  public onMetricChange(cb: () => void) {
    return this.metricSwitchChange.subscribe(cb);
  }

  /**
   * Determines if the given metric is relevance-like
   * (relevance sum, relevance avg, smart relevance)
   *
   * @param metric (string) metric to check
   * @returns True if the metric is relevance-like
   */
  public isMetricLikeRelevance(metric: MetricsAggregation): boolean {
    return this.relevanceMetrics.indexOf(metric) !== -1;
  }

  /**
   * Disable the smart relevance options when the number of feeds exceeds the threshold
   * (due to server load, we don't want to support those options for large data sets)
   *
   * @param totalFeeds Number of signals in the current results set
   */
  public updateComplexMetricsDisabledState(totalFeeds: number) {
    const environment = getEnvironment();
    const feedLimit = FEED_LIMIT[environment];
    const disabled = totalFeeds > feedLimit;
    this.complexMetricsDisabled.next(disabled);
  }
}
